import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChangeProposalStatus, LeadMessagePayload, LeadReceipt, LeadReceiptIssue, LeadSendRecord } from '../lib/api';
import { useArmedAfterMount, useConfirmGuard } from '../hooks/useConfirmGuard';

/**
 * LEADS1 - one lead-message draft. His rule: "full answers ... if i know where the app is getting the data from and
 * why its giving that answer" - so the WHY, what the lead last WROTE and every RECEIPT are on the card, and an
 * unsourced figure is red.
 *
 * ⭐ BUILT FOR HIS PHONE: the draft box grows to the whole text, long words wrap instead of scrolling the page
 *    sideways, and neither Approve nor the confirm can be passed by a double tap (see useConfirmGuard).
 * ⛔ Approve with unverified parts needs a second tap ("Send anyway"); the server refuses without it too.
 * ⛔⛔ What is approved is the SAVED draft: while the box says anything else, neither Approve nor Send anyway exists.
 * ⛔ A receipt's chip says what the check FOUND for that quote, never more - see `chipFor`.
 * ⚠️ The payload is a JSON column cast to a type - nothing here is proven by the compiler, so a missing list reads as
 *    empty and a source that is not a string is stringified rather than allowed to take the whole page down.
 */
const TRIGGER_LABEL: Record<LeadMessagePayload['trigger'], string> = {
  unanswered: 'Unanswered message',
  needs_decision: 'Needs your call',
  broken_promise: 'Broken promise',
  gone_quiet: 'Gone quiet',
};

const ISSUE_LABEL: Record<LeadReceiptIssue['kind'], string> = {
  unverified: 'Unverified - check before sending',
  mismatch: 'Does not match the records',
  bad_receipt: 'Bad receipt',
};

/** The server REFUSES a draft over 2,000 characters (a 400) - so the box stops there, where he can see it, first. */
const MAX_BODY = 2000;
/** The server cuts each of the lead's messages at this length; one this long was cut. */
const EXCERPT_CHARS = 300;

/**
 * Characters per row of the draft box on his iPhone. Measured from the layout, not guessed: at 375pt the text area is
 * ~261px (Layout px-4 + page px-4 + row p-4 + the box's own p-2 and border), ~32-34 characters of text-sm. 30 is on
 * the LOW side on purpose - the fallback errs toward one empty row too many, never a hidden line above Approve.
 */
const PHONE_CHARS_PER_ROW = 30;
/** Past any real draft (2,000 characters is 67 rows at 30 a row) - a cap must never hide the end of a long email. */
const MAX_ROWS = 150;

/**
 * The draft box's FIRST-PAINT rows (and jsdom's, which lays nothing out). The real size comes from the box's own
 * `scrollHeight` once it is on screen - see the layout effect in the card. Each line takes the rows its length needs
 * on a phone; at least 3, so an empty box still reads as a box.
 */
export const draftRows = (text: string): number =>
  Math.min(
    MAX_ROWS,
    Math.max(
      3,
      text.split('\n').reduce((rows, line) => rows + Math.max(1, Math.ceil(line.length / PHONE_CHARS_PER_ROW)), 0)
    )
  );

type Tone = 'green' | 'red' | 'amber' | 'gray';
const TONE_CLASS: Record<Tone, string> = {
  green: 'bg-green-50 text-green-800',
  red: 'bg-red-50 text-red-800',
  amber: 'bg-amber-50 text-amber-900',
  gray: 'bg-gray-100 text-gray-700',
};

/**
 * What the check found for ONE receipt, from the issues that name its quote.
 *
 * ⛔ Green only for a listing receipt the check COMPARED and found agreeing. A comparison that failed is red (it does
 *    not match) or amber (the app could not read the sentence) - never green because "it was checked".
 * ⛔ A listing receipt the check REFUSED (bad_receipt - a column it cannot compare, no listing holding it) compared
 *    nothing, so it is cited like any other source, and says the reason is below. Every other source is cited by
 *    design: the app holds no record to compare it with.
 * ⚠️ On a phone nothing hovers: the words carry the meaning, the title is only extra for a mouse.
 */
const chipFor = (r: LeadReceipt, issues: LeadReceiptIssue[], checked: boolean): { text: string; tone: Tone; title: string } => {
  if (r.sourceType !== 'current_listing') {
    return { text: 'cited - not checked', tone: 'gray', title: 'Named, not checked - the app holds no record to compare it with' };
  }
  // ⛔ No saved check is not a clean check: "no issues" only means "agrees" when a check actually ran.
  if (!checked) return { text: 'not checked', tone: 'gray', title: 'No receipt check was saved for this draft' };
  const mine = issues.filter((i) => i.text === r.quote);
  if (mine.some((i) => i.kind === 'bad_receipt')) {
    return {
      text: 'cited - not checked, see below',
      tone: 'gray',
      title: 'The app could not compare this with the listing - the reason is in red below',
    };
  }
  if (!r.field) {
    return { text: 'cited - not checked', tone: 'gray', title: 'Names no listing column, so the app did not compare it' };
  }
  if (mine.some((i) => i.kind === 'mismatch')) {
    return { text: 'does not match the listing', tone: 'red', title: 'The app compared this with the listing record and it differs' };
  }
  if (mine.some((i) => i.kind === 'unverified')) {
    return { text: 'checked - see below', tone: 'amber', title: 'Compared with the listing, but the app could not read the sentence - see below' };
  }
  return { text: 'matches the listing', tone: 'green', title: 'The app compared this with the listing record and it agrees' };
};

/** A time Joseph can read; an unreadable one is shown as stored rather than as "Invalid Date". */
const when = (at: string): string => (Number.isNaN(Date.parse(at)) ? String(at ?? '') : new Date(at).toLocaleString());

/** "Sent - GoHighLevel message <id> at <time>", or "Sent at <time>" when GoHighLevel gave no id. */
const sentLine = (r: LeadSendRecord): string => {
  const id = typeof r.ghlMessageId === 'string' ? r.ghlMessageId.trim() : '';
  const at = typeof r.sentAt === 'string' && r.sentAt ? ` at ${when(r.sentAt)}` : '';
  return `${id ? `Sent - GoHighLevel message ${id}` : 'Sent'}${at}`;
};

/** "Send anyway" counts as 'approve': when its row closes, the restored Approve slot is the one that says Working... */
type Tapped = 'approve' | 'edit' | 'handled' | 'snooze' | 'not_a_lead' | 'cancel';

interface Props {
  status: ChangeProposalStatus;
  payload: LeadMessagePayload;
  busy: boolean;
  onApprove: (confirmUnverified: boolean) => void;
  onEdit: (body: string) => Promise<unknown> | void;
  onSkip: (reason: 'handled' | 'snooze' | 'not_a_lead') => void;
  onCancel: () => void;
  /** The clock the double-tap guards read. Tests pass their own; the page leaves it to Date.now. */
  now?: () => number;
  /** Told on every keystroke whether the box now differs from the saved draft - the page remembers it per version. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Set by the page when a reload moved this draft while he had unsaved text in it (the remount cleared it). */
  editCleared?: boolean;
  /** The row's `applied_ref` - what the send worker recorded, once it has run. */
  sendRecord?: LeadSendRecord | null;
}

export default function LeadMessageCard({
  status, payload, busy, onApprove, onEdit, onSkip, onCancel, now, onDirtyChange, editCleared, sendRecord,
}: Props) {
  const [body, setBody] = useState(payload.body);
  const [tapped, setTapped] = useState<Tapped | null>(null);
  const confirm = useConfirmGuard(now);
  const appeared = useArmedAfterMount(now);
  const card = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const approveButton = useRef<HTMLButtonElement>(null);
  const notYet = useRef<HTMLButtonElement>(null);
  /** Where focus goes when the confirm closes: back to Approve after "Not yet", to the card after "Send anyway". */
  const focusAfter = useRef<'approve' | 'card' | null>(null);
  const edited = body !== payload.body;
  const checked = Boolean(payload.receiptCheck);
  const issues = payload.receiptCheck?.issues ?? [];
  const receipts = payload.receipts ?? [];
  const recent = payload.recentInbound ?? [];

  // The safe choice holds focus, so a second Enter (or an accidental tap's keyboard twin) lands on "Not yet"; when the
  // confirm closes, focus goes back where it came from rather than to the top of the page.
  useEffect(() => {
    if (confirm.open) {
      notYet.current?.focus();
      return;
    }
    if (focusAfter.current === 'approve') approveButton.current?.focus();
    else if (focusAfter.current === 'card') card.current?.focus();
    focusAfter.current = null;
  }, [confirm.open]);

  /**
   * ⭐ THE WHOLE DRAFT, ON HIS IPHONE: the box is set to its own scrollHeight as it mounts, on every change, and when
   *    the width changes (a phone turned sideways rewraps the text). `rows` is only the first paint. "auto" first, so
   *    the box can also SHRINK when text is deleted; the border (offset minus client) is added back because
   *    scrollHeight excludes it and the box is border-box.
   */
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [body]);

  /** The tapped button says it is working; the page's `busy` covers the whole card, so every button is disabled. */
  const label = (name: Tapped, text: string) => (busy && tapped === name ? 'Working...' : text);
  const tap = (name: Tapped, run: () => unknown) => {
    setTapped(name);
    run();
  };
  const plain = 'rounded border border-gray-300 px-3 py-1.5 text-gray-700 disabled:opacity-50';
  const to = payload.contactName?.trim() || 'the lead';

  return (
    <div
      ref={card}
      role="group"
      aria-label={`Lead message to ${to}`}
      tabIndex={-1}
      className="mt-2 min-w-0 space-y-2 text-sm focus:outline-none"
    >
      {editCleared && (
        <p className="rounded bg-amber-50 p-2 text-xs text-amber-900">
          This draft changed while you were editing - your unsaved text was cleared
        </p>
      )}
      <p className="break-words text-xs font-medium text-gray-500">
        {TRIGGER_LABEL[payload.trigger] ?? payload.trigger} · {payload.channel === 'sms' ? 'Text' : 'Email'}
        {payload.contactName ? ` to ${payload.contactName}` : ''}
        {payload.propertyLabel ? ` · ${payload.propertyLabel}` : ''}
      </p>
      <p className="break-words text-gray-800">{payload.why}</p>
      {payload.decisionQuestion && (
        <p className="break-words rounded bg-blue-50 p-2 text-blue-900">
          <span className="font-medium">Their question: </span>
          <span>{payload.decisionQuestion}</span>
        </p>
      )}
      {/* ⭐ No opt-out pattern catches every way a person says "stop". He reads what they wrote - the second check. */}
      {recent.length > 0 && (
        <div className="min-w-0 rounded bg-gray-50 p-2">
          <p className="text-xs font-medium text-gray-600">Their last messages</p>
          <ul aria-label="Their last messages" className="mt-1 space-y-1 text-xs text-gray-800">
            {recent.map((m, i) => (
              <li key={`${m.at}-${i}`} className="break-words">
                <span className="text-gray-500">{when(m.at)}</span>{' '}
                <span className="whitespace-pre-wrap break-words">
                  {m.body}
                  {String(m.body ?? '').length >= EXCERPT_CHARS ? '...' : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {payload.subject && (
        <p className="break-words text-gray-700">
          <span className="font-medium">Subject:</span> {payload.subject}
        </p>
      )}
      {/* ⭐ The WHOLE draft: rows for the first paint, then its own scrollHeight (the layout effect above). */}
      <textarea
        ref={box}
        aria-label={`Draft to ${to}`}
        value={body}
        onChange={(e) => {
          // ⛔ Typing closes an open confirm: undoing the edit must not bring back a "Send anyway" already armed.
          confirm.back();
          setBody(e.target.value);
          onDirtyChange?.(e.target.value !== payload.body);
        }}
        disabled={status !== 'proposed' || busy}
        rows={draftRows(body)}
        maxLength={MAX_BODY}
        className="w-full rounded border border-gray-300 p-2"
      />
      {receipts.length > 0 && (
        <ul className="space-y-1 text-xs text-gray-600">
          {receipts.map((r, i) => {
            const chip = chipFor(r, issues, checked);
            return (
              <li key={`${r.quote}-${r.sourceRef}-${i}`} className="break-words">
                "{r.quote}" - {String(r.sourceType ?? '').replace(/_/g, ' ')} {r.sourceRef}
                {r.field ? ` (${r.field})` : ''}
                {r.asOf ? `, as of ${r.asOf}` : ''}{' '}
                <span title={chip.title} className={`whitespace-nowrap rounded px-1 ${TONE_CLASS[chip.tone]}`}>
                  {chip.text}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {issues.length > 0 && (
        <ul className="rounded bg-red-50 p-2 text-xs text-red-800">
          {issues.map((i, n) => (
            <li key={`${i.kind}-${i.text}-${n}`} className="break-words">
              {ISSUE_LABEL[i.kind] ?? i.kind}: "{i.text}" ({i.detail})
            </li>
          ))}
        </ul>
      )}
      {/*
        ⛔ Read like the WORKER: only a literal `shadow: false` is live. A missing or odd flag is shadow - the worker will
           not send it, so the card must never say "Will send" about it.
      */}
      {status === 'approved' && payload.sendAfter && (
        <p className="text-xs text-blue-800">
          {payload.shadow !== false
            ? `Shadow mode - nothing goes out. Recorded as "would have sent" at ${when(payload.sendAfter)}`
            : `Will send at ${when(payload.sendAfter)} - Cancel send stops it until then`}
        </p>
      )}

      {/*
        ⭐ A REAL send names its GoHighLevel message id - his live proof checks it in GoHighLevel. Only a literal
           `shadow: false` record is a send; a shadow one keeps the page's outcome sentence instead.
      */}
      {status === 'applied' && sendRecord?.shadow === false && (
        <p className="break-words rounded bg-green-50 p-2 text-xs text-green-800">
          {sentLine(sendRecord)}
        </p>
      )}

      {status === 'proposed' &&
        (!edited && confirm.open ? (
          /*
            ⛔ ITS OWN ROW, and it REPLACES the action row: the slot the Approve tap landed on now holds the sentence
               and "Not yet", never "Send anyway". "Send anyway" also ignores taps until the guard has passed.
          */
          <div role="group" aria-label="Confirm sending" className="rounded border border-red-300 bg-red-50 p-2">
            <p className="text-xs text-red-800">
              Parts of this draft are unverified or do not match the records. Send it anyway?
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                ref={notYet}
                type="button"
                disabled={busy}
                onClick={() => {
                  focusAfter.current = 'approve';
                  confirm.back();
                }}
                className={`${plain} bg-white`}
              >
                Not yet
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (!confirm.armed() || !appeared()) return;
                  focusAfter.current = 'card';
                  confirm.back();
                  tap('approve', () => onApprove(true));
                }}
                className="rounded bg-red-600 px-3 py-1.5 text-white disabled:opacity-50"
              >
                Send anyway
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {edited ? (
              <button
                type="button"
                disabled={busy || !body.trim()}
                onClick={() => tap('edit', () => void onEdit(body))}
                className="rounded bg-gray-900 px-3 py-1.5 text-white disabled:opacity-50"
              >
                {label('edit', 'Save edit')}
              </button>
            ) : (
              <button
                ref={approveButton}
                type="button"
                disabled={busy}
                onClick={() => {
                  // ⛔ Ignored until the card has been on screen the whole guard - see useArmedAfterMount.
                  if (!appeared()) return;
                  if (issues.length) confirm.ask();
                  else tap('approve', () => onApprove(false));
                }}
                className="rounded bg-green-600 px-3 py-1.5 text-white disabled:opacity-50"
              >
                {label('approve', 'Approve')}
              </button>
            )}
            <button type="button" disabled={busy} onClick={() => tap('handled', () => onSkip('handled'))} className={plain}>
              {label('handled', 'Handled already')}
            </button>
            <button type="button" disabled={busy} onClick={() => tap('snooze', () => onSkip('snooze'))} className={plain}>
              {label('snooze', 'Snooze 3 days')}
            </button>
            <button type="button" disabled={busy} onClick={() => tap('not_a_lead', () => onSkip('not_a_lead'))} className={plain}>
              {label('not_a_lead', 'Not a real lead')}
            </button>
          </div>
        ))}
      {status === 'approved' && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => tap('cancel', onCancel)}
            className="rounded border border-red-300 px-3 py-1.5 text-red-700 disabled:opacity-50"
          >
            {label('cancel', 'Cancel send')}
          </button>
        </div>
      )}
    </div>
  );
}
